// Collapse state for the sidebar rail: the cookie-backed preference, the
// narrow-viewport auto-collapse and a page's forced fold. Split out of
// Sidebar.tsx (BL-2422).

import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@auctogon/ui/web';
import { SIDEBAR_COLLAPSED_COOKIE } from '@auctogon/admin-isomorphic/isomorphic/constants/sidebar';
import { useSidebarForcedCollapsed } from '../SidebarAutoCollapse';

// BUG-430: below this width (iPad portrait and narrower) the expanded rail
// consumes a third of the viewport, so the sidebar auto-collapses to icons.
// 1024px is the documented minimum supported width (ui-standards.md → Misc);
// at or above it the operator's saved preference wins.
const NARROW_VIEWPORT_QUERY = '(max-width: 1023px)';

function writeCollapsedCookie(collapsed: boolean) {
  try {
    const oneYear = 60 * 60 * 24 * 365;
    document.cookie = `${SIDEBAR_COLLAPSED_COOKIE}=${collapsed ? '1' : '0'}; path=/; max-age=${oneYear}; samesite=lax`;
  } catch {
    // cookies unavailable — ignore
  }
}

/**
 * Collapse state for the sidebar rail (BUG-430).
 *
 * Auto-collapses to the icon rail when the viewport drops below the 1024px
 * support floor (iPad portrait / narrow split-screen) and restores the saved
 * preference when it grows back. A manual toggle at a wide viewport persists
 * to the cookie; a toggle while narrow is a transient, session-local override
 * (the rail auto-collapsed — expanding it there says nothing about the
 * operator's desktop preference) that the next boundary crossing discards.
 *
 * @param initialCollapsed - The cookie-backed preference from the server render.
 * @returns The current collapsed state and the manual toggle handler.
 */
export function useSidebarCollapse(initialCollapsed: boolean) {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  // The operator's explicit choice (cookie-backed). The viewport-driven
  // auto-collapse must never overwrite it — growing back past the boundary
  // restores exactly this preference.
  const collapsePrefRef = useRef(initialCollapsed);
  // Reactive read of the narrow-viewport tier (the shared matchMedia hook).
  const isNarrow = useMediaQuery(NARROW_VIEWPORT_QUERY);
  // BL-1987: a page may ask the nav to fold while it needs the width (the docked
  // lot panel at 1024–1679px, BL-2094). Like the narrow tier, that never touches the
  // saved preference, and the operator can still open the nav for the session.
  const forced = useSidebarForcedCollapsed();

  const toggleCollapsed = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    // A toggle while narrow is a transient, session-local override that the next
    // boundary crossing discards, so only a wide-viewport toggle persists the
    // preference. Read matchMedia LIVE here, not the render-cached `isNarrow`:
    // the persistence decision needs click-time accuracy, and `isNarrow` starts
    // at the `() => false` server snapshot on every SSR/hydration mount and only
    // self-corrects in a post-hydration effect — a click in that window on a
    // narrow-viewport load would otherwise persist the 1-year collapse cookie as
    // a bogus wide-viewport preference.
    if (!forced && !window.matchMedia(NARROW_VIEWPORT_QUERY).matches) {
      collapsePrefRef.current = next;
      writeCollapsedCookie(next);
    }
  };

  // Auto-collapse below the support floor; restore the saved preference above
  // it. Runs on mount (the first real reading after hydration, when `isNarrow`
  // settles from its `false` server snapshot) and on every band crossing.
  useEffect(() => {
    setIsCollapsed(isNarrow || forced ? true : collapsePrefRef.current);
  }, [isNarrow, forced]);

  return { isCollapsed, toggleCollapsed };
}
